// This file is part of re-motion
// Copyright (c) rubicon IT GmbH, www.rubicon.eu
// 
import { IRemoteLogMessageSender } from "@lib/logging/IRemoteLogMessageSender";
import { LogLevel } from "@lib/logging/LogLevel";

type LogEntry = {
  logLevel: LogLevel;
  format: string;
  args: any[];
};

/**
 * Provides methods for buffering log messages in an array.
 * After the buffer is full the oldest messages are replaced (like a rolling file log).
 */
export class BufferedRemoteLogMessageSender implements IRemoteLogMessageSender
{
  private buffer: LogEntry[] = [];
  private skipLogging = false;

  readonly capacity: number;

  constructor(capacity: number)
  {
    this.capacity = capacity;
  }

  get count(): number
  {
    return this.buffer.length;
  }

  clear(): void
  {
    this.buffer = [];
  }

  flush(remoteMessageSender: IRemoteLogMessageSender): void
  {
    this.skipLogging = true;
    try
    {
      const buffer = this.buffer;
      if (buffer.length > 0)
        for (const logEntry of buffer)
          remoteMessageSender.sendLogMessage(logEntry.logLevel, logEntry.format, logEntry.args);
    }
    finally
    {
      this.skipLogging = false;
    }
  }

  sendLogMessage(logLevel: LogLevel, format: string, args: any[]): void
  {
    if (this.skipLogging)
      return;

    const diff = this.buffer.length - this.capacity;
    if (diff > 0)
      this.buffer.splice(0, diff);

    const logEntry: LogEntry = {
      args: args,
      format: format,
      logLevel: logLevel
    };

    this.buffer.push(logEntry);
  }
}